import React from 'react';
import SectionHead from './SectionHead';
import Button from './Button';
import externalLinkProps from './externalLinkProps';

/**
 * `.prix` — grille de forfaits.
 *
 * Piège n°6 : la maquette mettait en avant la deuxième carte via
 * `:nth-child(2)` et inscrivait « TOP DES VENTES » dans un `content:` CSS.
 * `isFeatured` et `ribbon` sont désormais portés par les données, ce qui
 * autorise n'importe quel nombre de cartes dans n'importe quel ordre.
 *
 * Spec 2.d/3.e : chaque vignette prix doit porter un lien. Le contrat
 * richfield `NEWSFEED` n'offre que trois champs texte (`title`/`desc`/
 * `cta_label`), déjà pris par le nom du forfait, la liste des prestations et
 * le prix — aucun champ libre pour un libellé de lien dédié. `href` rend donc
 * toute la carte cliquable plutôt qu'un lien texte séparé : `cta_target` cesse
 * d'être un champ saisissable dans le BO qui ne fait rien, et le `:hover`
 * qui soulève déjà la carte en CSS devient honnête. Sans `href` (contenu
 * statique, ou champ non renseigné), la carte reste un `<div>` — comportement
 * identique à avant.
 */
export const PriceCard = ({ image, title, desc, badge, isFeatured, ribbon, href }) => {
  const className = ['prix-card', isFeatured ? 'is-featured' : null].filter(Boolean).join(' ');
  const content = (
    <>
      {ribbon && <span className="prix-card-ribbon">{ribbon}</span>}
      {image && (
        <div className="disc">
          <img src={image} alt="" width="260" height="234" loading="lazy" />
        </div>
      )}
      <h3>{title}</h3>
      {desc && <p className="desc">{desc}</p>}
      {badge && <div className="badge-prix">{badge}</div>}
    </>
  );

  if (href) {
    return (
      <a className={className} href={href} {...externalLinkProps(href)}>
        {content}
      </a>
    );
  }

  return <div className={className}>{content}</div>;
};

const PriceGrid = ({
  eyebrow,
  eyebrowVariant = 'teal',
  title,
  subtitle,
  cards = [],
  cta,
  centered = true,
  withSection = true,
  headingLevel,
}) => {
  if (!cards.length) return null;

  const body = (
    <>
      <SectionHead
        eyebrow={eyebrow}
        eyebrowVariant={eyebrowVariant}
        title={title}
        subtitle={subtitle}
        centered={centered}
        headingLevel={headingLevel}
      />
      <div className="prix-grid">
        {cards.map((card, index) => (
          <PriceCard key={card.title || index} {...card} />
        ))}
      </div>
      {cta && (
        <div className="prix-cta">
          <Button variant="yellow" href={cta.href} withArrow>
            {cta.label}
          </Button>
        </div>
      )}
    </>
  );

  // Dans un onglet, la carte n'est pas une section de page : pas de `.prix`.
  if (!withSection) return body;

  return (
    <section className="prix" id="forfaits">
      <div className="container">{body}</div>
    </section>
  );
};

export default PriceGrid;
